import { createTheme } from "@mui/material/styles";
import { brand, slate, semantic, radius, shadow, surface, transition } from "./theme/tokens";

const theme = createTheme({
  palette: {
    primary: { main: brand[500], dark: brand[600], light: brand[300], contrastText: "#fff" },
    success: semantic.success,
    error: semantic.error,
    warning: semantic.warning,
    info: semantic.info,
    text: { primary: slate[800], secondary: slate[500], disabled: slate[400] },
    divider: surface.border,
    background: {
      default: surface.subtle,
      paper: surface.card,
      subtle: surface.subtle,
      sunken: surface.sunken,
    },
    grey: slate,
    brand,
    surface,
  },
  shape: { borderRadius: radius.md },
  typography: {
    fontFamily: [
      "Inter",
      "-apple-system",
      "BlinkMacSystemFont",
      "Segoe UI",
      "Roboto",
      "Helvetica Neue",
      "sans-serif",
    ].join(","),
    h1: { fontWeight: 800, letterSpacing: "-0.03em" },
    h2: { fontWeight: 800, letterSpacing: "-0.025em" },
    h3: { fontWeight: 700, letterSpacing: "-0.02em" },
    h4: { fontWeight: 700, letterSpacing: "-0.02em" },
    h5: { fontWeight: 700, letterSpacing: "-0.015em" },
    h6: { fontWeight: 600, letterSpacing: "-0.01em" },
    subtitle1: { letterSpacing: "-0.005em" },
    subtitle2: { letterSpacing: "0.01em" },
    button: { textTransform: "none", fontWeight: 600, letterSpacing: "0.01em" },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background-color: ${surface.subtle};
          color: ${slate[800]};
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
      `,
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          fontWeight: 600,
          transition: `all ${transition.fast} ease`,
          "&:hover": {
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        rounded: { borderRadius: radius.md },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          border: `1px solid ${surface.border}`,
          boxShadow: shadow.card,
          transition: `box-shadow ${transition.normal} ease, border-color ${transition.normal} ease`,
          "&:hover": {
            boxShadow: shadow.cardHover,
            borderColor: surface.borderHover,
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: surface.subtle,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          color: slate[600],
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          borderBottom: `1px solid ${surface.border}`,
          backgroundColor: surface.subtle,
        },
        root: {
          borderBottom: `1px solid ${surface.border}`,
          color: slate[800],
          fontSize: "0.85rem",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: `background-color ${transition.fast} ease`,
          "&.MuiTableRow-hover:hover": {
            backgroundColor: `${surface.subtle} !important`,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          border: `1px solid ${surface.border}`,
          "&:before": { display: "none" },
          "&.Mui-expanded": {
            margin: "0 0 12px 0",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: `${radius.lg}px`,
          boxShadow: shadow.modal,
          border: `1px solid ${surface.border}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: `${radius.md}px`,
          boxShadow: shadow.modal,
          border: `1px solid ${surface.border}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: `${radius.pill}px`,
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&:focus-visible": {
            outline: `2px solid ${brand[500]}`,
            outlineOffset: 2,
          },
        },
      },
    },
  },
  customShadows: shadow,
});

export default theme;
